import { gozokiApiWithAuth } from '../gozokiApi'

// Destroy a store
export const destroyStore = async (id: number) => {
  const response = await gozokiApiWithAuth.delete(`/stores/${id}`)
  return response
}

// Destroy multiple stores
export const destroyStores = async (ids: number[]) => {
  const responses = await Promise.all(ids.map((id) => destroyStore(id)))
  return responses
}

export const refreshStocks = async () => {
  const response = await gozokiApiWithAuth.post(`/sync/stock`)
  return response
}

export const refreshBearcod = async () => {
  const response = await gozokiApiWithAuth.post(`/sync/update-bearcod`)
  return response
}

export const downloadAdminXlsx = async ({
  dateStart,
  dateEnd,
  storeId,
}: {
  dateStart: string
  dateEnd: string
  storeId?: number
}) => {
  const response = await gozokiApiWithAuth.get(
    `/stores/get-audit-xlsx/${dateStart}/${dateEnd}${storeId ? `?storeId=${storeId}` : ''}`,
    {
      responseType: 'blob',
    },
  )
  return response
}

export const downloadDeliveryReport = async ({
  dateStart,
  dateEnd,
  storeId,
}: {
  dateStart: string
  dateEnd: string
  storeId?: number
}) => {
  const params = new URLSearchParams()
  if (storeId) {
    params.append('storeId', storeId.toString())
  }
  const queryString = params.toString()
  const response = await gozokiApiWithAuth.get(
    `/stores/get-delivery-report/${dateStart}/${dateEnd}${queryString ? `?${queryString}` : ''}`,
    {
      responseType: 'blob',
    },
  )
  return response
}

export const downloadPaymentsCsv = async ({
  dateStart,
  dateEnd,
}: {
  dateStart: string
  dateEnd: string
}) => {
  const response = await gozokiApiWithAuth.get(
    `/stores/get-payments-xlsx/${dateStart}/${dateEnd}`,
    {
      responseType: 'blob',
      // A wide payments export can legitimately take more than the API client's
      // default two minutes. Keep this request alive until the archive is fully sent.
      timeout: 0,
    },
  )
  return response
}

export const downloadBasketsAccountingCsv = async ({
  dateStart,
  dateEnd,
  includeHeader = true,
}: {
  dateStart: string
  dateEnd: string
  includeHeader?: boolean
}) => {
  const response = await gozokiApiWithAuth.get(
    `/stores/get-baskets-accounting-export-csv/${dateStart}/${dateEnd}`,
    {
      params: { includeHeader },
      responseType: 'blob',
      // A large accounting export can legitimately take more than the API client's
      // default two minutes. Keep this request alive until the streamed response ends.
      timeout: 0,
    },
  )
  return response
}

export const downloadRevenueIntegrationCsv = async ({
  dateStart,
  dateEnd,
}: {
  dateStart: string
  dateEnd: string
}) => {
  const response = await gozokiApiWithAuth.get(
    `/stores/get-revenue-integration-csv/${dateStart}/${dateEnd}`,
    // Streamé jour par jour côté serveur : un exercice complet dépasse le délai par défaut.
    { responseType: 'blob', timeout: 0 },
  )
  return response
}

export const downloadSupplyReport = async ({
  dateStart,
  dateEnd,
}: {
  dateStart: string
  dateEnd: string
}) => {
  const response = await gozokiApiWithAuth.get(`/stores/supply-report/${dateStart}/${dateEnd}`, {
    responseType: 'blob',
  })
  return response
}

export const rebootCpiKiosk = async (kioskId: string) => {
  const response = await gozokiApiWithAuth.post(`/stores/reboot-kiosk/${kioskId}`)
  return response
}

export const downloadTicketsCsv = async ({
  dateStart,
  dateEnd,
  storeId,
}: {
  dateStart: string
  dateEnd: string
  storeId?: number | null
}) => {
  const response = await gozokiApiWithAuth.get('/stores/get-all-tickets-xlsx', {
    params: {
      startDate: dateStart,
      endDate: dateEnd,
      ...(storeId ? { storeId } : {}),
    },
    responseType: 'blob',
  })
  return response
}

export const importReapproNeedModificationsBulk = async (file: File) => {
  const formData = new FormData()
  formData.append('importFile', file)

  const response = await gozokiApiWithAuth.post<{
    updated: number
    unchanged: number
    rejected: number
    errors: { reference: string; message: string }[]
  }>('/stores/reappro-needs/import-modifications-bulk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}
